import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="privacy-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Button>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-zinc-900">Política de Privacidad</h1>
          </div>
          <p className="text-zinc-600 mt-2">Última actualización: Marzo 2026</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary" />
              Información que Recopilamos
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>
              <strong>Spin 360° Fusion2V</strong> recopila la siguiente información cuando utilizas nuestra aplicación:
            </p>
            <ul>
              <li><strong>Información de la tienda:</strong> ID de tienda, nombre, URL y token de acceso proporcionado por Tiendanube.</li>
              <li><strong>Información de productos:</strong> Datos de productos obtenidos a través de la API de Tiendanube para mostrar la lista de productos disponibles.</li>
              <li><strong>Configuraciones:</strong> URLs de spins 360° y configuraciones de hotspots que guardas para cada producto.</li>
              <li><strong>Datos de uso:</strong> Métricas anónimas sobre visualizaciones e interacciones con los spins 360° para fines de analytics.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              Cómo Usamos la Información
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>Utilizamos la información recopilada para:</p>
            <ul>
              <li>Proporcionar y mantener el servicio de visualización 360°.</li>
              <li>Mostrar tus productos y permitir la configuración de spins.</li>
              <li>Generar reportes de analytics sobre el rendimiento de tus productos.</li>
              <li>Mejorar y optimizar nuestra aplicación.</li>
              <li>Comunicarnos contigo sobre actualizaciones o soporte técnico.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Seguridad de los Datos
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>
              La seguridad de tus datos es importante para nosotros. Implementamos medidas de seguridad 
              estándar de la industria para proteger tu información:
            </p>
            <ul>
              <li>Conexiones cifradas mediante HTTPS.</li>
              <li>Tokens de acceso almacenados de forma segura.</li>
              <li>Acceso restringido solo a los datos necesarios para el funcionamiento de la app.</li>
              <li>No compartimos tu información con terceros, excepto con Sirv para el servicio de visualización 360°.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Servicios de Terceros</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>Nuestra aplicación utiliza los siguientes servicios de terceros:</p>
            <ul>
              <li><strong>Tiendanube:</strong> Para autenticación y acceso a datos de tu tienda.</li>
              <li><strong>Sirv:</strong> Para almacenamiento y visualización de imágenes 360°.</li>
            </ul>
            <p>
              Cada uno de estos servicios tiene sus propias políticas de privacidad que te recomendamos revisar.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tus Derechos</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>Tienes derecho a:</p>
            <ul>
              <li>Acceder a los datos que tenemos sobre tu tienda.</li>
              <li>Solicitar la corrección de datos incorrectos.</li>
              <li>Solicitar la eliminación de tus datos desinstalando la aplicación.</li>
              <li>Revocar el acceso a tu tienda en cualquier momento desde el panel de Tiendanube.</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              Contacto
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>
              Si tienes preguntas sobre esta Política de Privacidad, puedes contactarnos en:
            </p>
            <ul>
              <li><strong>Email:</strong> info@fusionpublicidad.com.ar</li>
              <li><strong>Web:</strong> www.fusionpublicidad.com.ar</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cambios en esta Política</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-zinc max-w-none">
            <p>
              Podemos actualizar nuestra Política de Privacidad ocasionalmente. Te notificaremos 
              cualquier cambio publicando la nueva política en esta página y actualizando la 
              fecha de "última actualización".
            </p>
          </CardContent>
        </Card>

      </main>

      <footer className="bg-white border-t border-zinc-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-zinc-600">
          <p>© 2026 Spin 360° Fusion2V. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
